/* eslint-disable max-len */
import {
    useEffect, useState, useMemo, useCallback,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';
import './ProFormaWizardStepThree.scss';

import { useAuthContext } from 'contexts/AuthContext';
import { NSCard, NSSwitch } from 'bricks';
import { ProFormaModelIds } from 'constants/proForma';
import { useProFormaWizard } from 'views/ProFormaWizard/context/ProFormaWizardProvider';
import { INITIAL_EXPENSES } from 'views/ProFormaWizard/constants';
import { formatUSDCurrencyTwoDigits } from 'ns_libs/formatter';
import RenderIf from 'components/RenderIf/RenderIf';
import { IExpense } from 'views/ProFormaWizard/BasicInformation/hooks/useExpenses';
import { useGetProFormaWizardStep } from 'views/ProFormaWizard/hooks/useGetProFormaWizardStep';
import { useUpdateProFormaWizardStep } from 'views/ProFormaWizard/hooks/useUpdateProFormaWizardStep';
import ReadOnlyWrapper from 'components/ReadOnlyWrapper/ReadOnlyWrapper';
import { IProFormaWizardStep3 } from 'views/ProFormaWizard/types';
import { formatStepThreeData } from 'views/ProFormaWizard/helpers/formatStepData';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import { getOperationsWizardDiscrepancies } from 'views/ProFormaTable/helpers';
import { OPERATIONS_EXPANDABLE_SECTIONS } from 'views/ProFormaTable/constants';
import BOEDiscrepancyIcon from 'views/ProFormaTable/components/BOEDiscrepancyIcon/BOEDiscrepancyIcon';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { getWizardOperationsIncomeTotals } from 'views/ProFormaWizard/helpers';
import { isEmpty } from 'lodash';
import { useOtherIncomeManager } from './hooks/useOtherIncomeManager';
import {
    ExpenseType, IExpenses, ITenant, IUnitMix,
} from './types';
import OperatingInfo, { OperatingInfoValues } from './components/OperatingInfo/OperatingInfo';
import Expenses from './components/Expenses/Expenses';
import { getDefaultCreditLoss, getDefaultVacancy } from './helpers';
import { useUnitManager } from './hooks/useUnitManager';
import UnitMixOverviewCard from './components/UnitMixOverviewCard/UnitMixOverviewCard';
import TenantsOverviewCard from './components/TenantsOverviewCard/TenantsOverviewCard';
import OtherIncomeCard from './components/OtherIncomeCard/OtherIncomeCard';

export interface IProFormaWizardStepThreeProps {
    boeScenario?: BOEScenario;
}

const ProFormaWizardStepThree = ({ boeScenario }: IProFormaWizardStepThreeProps) => {
    const { selectedOrganizationId } = useAuthContext();
    const { registerStepHandler, proForma, isLoading: isLoadingProvider } = useProFormaWizard();
    const { proFormaId } = useParams<{ proFormaId: string }>();

    const proFormaModelId = proForma?.proFormaModelId || ProFormaModelIds.APARTMENT;
    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId);

    const [operatingInfoValues, setOperatingInfoValues] = useState<OperatingInfoValues>({} as OperatingInfoValues);
    const [expensesValues, setExpensesValues] = useState<IExpenses>({
        expenses: 1,
        amountPerYear: 0,
        expensesType: 'sf' as ExpenseType,
        isDeleteAllowed: true,
    });
    const [expensesDetail, setExpensesDetail] = useState<IExpense[]>([]);
    const [unitArray, setUnitArray] = useState<IUnitMix[]>([]);
    const [tenantArray, setTenantArray] = useState<ITenant[]>([]);
    const [showExpenseDetailToggle, setShowExpenseDetailToggle] = useState(false);
    const [showOtherIncomeToggle, setShowOtherIncomeToggle] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    const { data: stepData, isLoading: isStepDataLoading } = useGetProFormaWizardStep(
        selectedOrganizationId!, proFormaId || '', 3,
    ) as { data: IProFormaWizardStep3 | undefined, isLoading: boolean };

    const initialOtherIncome = useMemo(() => {
        if (!stepData?.otherIncome?.length) {
            return [];
        }
        return stepData.otherIncome;
    }, [stepData]);

    const {
        handleDuplicateOrAdd, handleDelete, handleUpdate,
    } = useUnitManager({
        unitArray,
        tenantArray,
        setUnitArray,
        setTenantArray,
        initialUnits: stepData?.incomeUnitMixes || [],
        initialTenants: stepData?.incomeTenants || [],
        isApartment,
    });

    const {
        incomeArray, handleAddOrDuplicateIncome, handleDeleteIncome, handleUpdateIncome,
    } = useOtherIncomeManager({
        initialIncomes: initialOtherIncome,
    });

    const incomeUnitTotals = getWizardOperationsIncomeTotals(isApartment, unitArray || [], tenantArray || []);
    const totalOtherIncome = useMemo(() => incomeArray.reduce((sum, unit) => sum + unit.amountPerYear!, 0), [incomeArray]);
    const toggleCalculate = useCallback(() => setShowOtherIncomeToggle(prev => !prev), []);

    const validate = useCallback(() => {
        const errors: string[] = [];

        if (!operatingInfoValues.occupancyUponStabilizationPercentage) errors.push('Occupancy upon stabilization is required');
        if (!operatingInfoValues.rentalGrowthPercentage) errors.push('Rental growth percentage is required');
        if (!operatingInfoValues.percentPreLeased) errors.push('Percent pre-leased is required');
        if (!operatingInfoValues.leaseUpPace) errors.push('Lease-up pace is required');
        if (!operatingInfoValues.lengthOfLease) errors.push('Length of lease is required');
        if (!operatingInfoValues.initialFreeRent) errors.push('Initial free rent is required');
        if (!operatingInfoValues.stabilizedFreeRent) errors.push('Stabilized free rent is required');
        if (!operatingInfoValues.generalVacancy) errors.push('Vacancy is required');
        if (!operatingInfoValues.creditLoss) errors.push('Credit loss is required');

        // Validate unit mix (at least one unit required)
        if (unitArray.length === 0) {
            const entityName = isApartment ? 'unit mix' : 'tenant';
            errors.push(`At least one ${entityName} item is required`);
        }

        if (isApartment) {
            unitArray.forEach((unit, index) => {
                if (!unit.averageSquareFootage) errors.push(`Unit ${index + 1}: Average square footage is required`);
                if (!unit.amountPerMonth) errors.push(`Unit ${index + 1}: Amount per month is required`);
                if (!unit.units) errors.push(`Unit ${index + 1}: Number of units is required`);
                if ((unit as IUnitMix).bedrooms === undefined) errors.push(`Unit ${index + 1}: Number of bedrooms is required`);
                if ((unit as IUnitMix).bathrooms === undefined) errors.push(`Unit ${index + 1}: Number of bathrooms is required`);
            });
        } else {
            tenantArray.forEach((tenant, index) => {
                if (!tenant.suite) errors.push(`Tenant ${index + 1}: Suite is required`);
                if (!tenant.squareFootage) errors.push(`Tenant ${index + 1}: Average square footage is required`);
                if (!tenant.rentGrowth) errors.push(`Tenant ${index + 1}: Rent growth is required`);
                if (!tenant.valuePerSfPerYear) errors.push(`Tenant ${index + 1}: Rent per square foot per year is required`);
                if (!tenant.leaseStart) errors.push(`Tenant ${index + 1}: Lease start date is required`);
                if (!tenant.rentStart) errors.push(`Tenant ${index + 1}: Rent start date is required`);
                if (!tenant.recoveryPercentage) errors.push(`Tenant ${index + 1}: Recovery income percentage is required`);
                if (!tenant.recoveryStart) errors.push(`Tenant ${index + 1}: Recovery start date is required`);
            });
        }

        // Validate other income if toggled
        if (showOtherIncomeToggle) {
            if (incomeArray.length === 0) {
                errors.push('At least one other income item is required when other income is enabled');
            } else {
                incomeArray.forEach((income, index) => {
                    if (!income.name) errors.push(`Income ${index + 1}: Name is required`);
                    if (!income.amountPerYear) errors.push(`Income ${index + 1}: Amount per year is required`);
                    if (!income.valuePerUnitPerMonth) errors.push(`Income ${index + 1}: Value per unit per month is required`);
                    if (typeof income.annualGrowth !== 'number') errors.push(`Income ${index + 1}: Annual growth is required`);
                });
            }
        }

        // Validate expenses based on toggle state
        if (showExpenseDetailToggle) {
            if (expensesDetail.length === 0) {
                errors.push('At least one expense detail is required when expense detail is enabled');
            }
            expensesDetail.forEach((expense, index) => {
                if (!expense.name) errors.push(`Expense ${index + 1}: Name is required`);
                if (!expense.amountPerYear && expense.amountPerYear !== 0) errors.push(`Expense ${index + 1}: Amount per year is required`);
                if (typeof expense.annualGrowth !== 'number') errors.push(`Expense ${index + 1}: Annual growth is required`);
            });
        } else {
            if (!expensesValues.expenses || expensesValues.expenses <= 0) {
                errors.push('Expenses value is required when expense detail is disabled');
            }
            if (!expensesValues.amountPerYear || expensesValues.amountPerYear <= 0) {
                errors.push('Total expenses value is required when expense detail is disabled');
            }
        }

        setValidationErrors(errors);
        return errors.length === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitArray, showOtherIncomeToggle, incomeArray, showExpenseDetailToggle, expensesValues, expensesDetail]);

    const operatingExpensesTotal = showExpenseDetailToggle
        ? expensesDetail.reduce((acc, curr) => acc + Number(curr.amountPerYear), 0)
        : expensesValues.amountPerYear;

    const boeDiscrepancies = boeScenario
        ? getOperationsWizardDiscrepancies(
            proFormaModelId, incomeUnitTotals.totalIncomeUnits, unitArray.length,
            incomeUnitTotals.totalRentalIncome, operatingExpensesTotal, boeScenario,
        ) : undefined;

    const updateStep = useUpdateProFormaWizardStep();

    const getData = useCallback((isDraft?: boolean) => {
        const data = formatStepThreeData({
            operatingInfoValues,
            unitArray: isApartment ? unitArray : [],
            tenantArray: !isApartment ? tenantArray : [],
            expensesDetail,
            incomeArray,
            expensesValues,
            showExpenseDetailToggle,
            showOtherIncomeToggle,
            isDraft,
            proFormaStartDate: proForma.startDate,
        });

        return new Promise((resolve, reject) => {
            updateStep.mutate({
                orgId: selectedOrganizationId!,
                proFormaId: proFormaId || '',
                stepNumber: 3,
                data,
            }, {
                onSuccess: result => resolve(result),
                onError: error => reject(error),
            });
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitArray, tenantArray, expensesDetail, incomeArray, operatingInfoValues, showOtherIncomeToggle, showExpenseDetailToggle, expensesValues, selectedOrganizationId, proFormaId, updateStep]);

    useEffect(() => {
        if (stepData) {
            setOperatingInfoValues({
                occupancyUponStabilizationPercentage: Number(stepData?.occupancyUponStabilizationPercentage ?? 0),
                rentalGrowthPercentage: Number(stepData.rentalGrowthPercentage || 0),
                percentPreLeased: stepData.percentPreLeased ?? 0,
                leaseUpPace: stepData.leaseUpPace ?? 0,
                lengthOfLease: stepData.lengthOfLease ?? 0,
                initialFreeRent: stepData.initialFreeRent ?? 0,
                stabilizedFreeRent: stepData.stabilizedFreeRent ?? 0,
                generalVacancy: Number(stepData.generalVacancy || 0) || getDefaultVacancy(proFormaModelId),
                creditLoss: Number(stepData.creditLoss || 0) || getDefaultCreditLoss(proFormaModelId),
            });
            setExpensesDetail(stepData.operatingExpenses?.length ? stepData.operatingExpenses.map(expense => ({
                id: expense.id,
                name: expense.name,
                annualGrowth: Number(expense.annualGrowth),
                amountPerYear: Number(expense.amountPerYear),
                isDeleteAllowed: expense.isDeleteAllowed,
                operationExpenseType: expense.operationExpenseType,
                calculatedOn: expense.calculatedOn,
                percentageOfEffectiveGrossRevenue: expense.percentageOfEffectiveGrossRevenue,
            } as IExpense)) : INITIAL_EXPENSES);
            setShowExpenseDetailToggle(!!stepData?.operatingExpenses?.length);
            setShowOtherIncomeToggle(!!stepData.otherIncome?.length);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepData]);

    useEffect(() => {
        registerStepHandler(3, {
            validate,
            getData,
        });
    }, [registerStepHandler, validate, getData]);

    return (
        <RenderIf isTrue={stepData} isLoading={isStepDataLoading}>
            <ReadOnlyWrapper isReadOnly={isLoadingProvider}>
                <RenderIf isTrue={validationErrors?.length}>
                    <NSCard className="mt-2 p-2">
                        <div className="text-danger">
                            <div className="font-weight-bold mb-2">Please fix the following errors:</div>
                            <ul className="pl-4 mb-0">
                                {validationErrors.map(error => (
                                    <li key={`error-${error.toLowerCase().replace(/\s+/g, '-')}`}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    </NSCard>
                </RenderIf>

                <RenderIf isTrue={!isEmpty(operatingInfoValues)}>
                    <OperatingInfo
                        setOperatingInfoValues={setOperatingInfoValues}
                        values={operatingInfoValues!}
                    />
                </RenderIf>

                <div className="ProFormaWizardStepOne__separator" />

                {/* Income */}
                <div className="d-flex">
                    <div>
                        <div className="ProFormaWizardStepOne__title">Income</div>
                        <div className="ProFormaWizardStepOne__subtitle">Overview of this investment's total income</div>
                    </div>
                    <RenderIf isTrue={boeDiscrepancies ? !!boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[1].number]?.length : false}>
                        <BOEDiscrepancyIcon
                            section={OPERATIONS_EXPANDABLE_SECTIONS[1].title}
                            metricNames={boeDiscrepancies ? boeDiscrepancies[OPERATIONS_EXPANDABLE_SECTIONS[1].number] : []}
                            className="ml-2 my-auto"
                        />
                    </RenderIf>
                </div>
                <NSCard className="mt-2 p-2">
                    <div className="ProFormaWizardStepThree__unitTitle mb-2">
                        {isApartment ? 'Unit Mix' : 'Tenants'}
                    </div>
                    <div className="row">
                        <RenderIf isTrue={isApartment}>
                            {unitArray.map(unit => (
                                <div className="col-md-4" key={unit.id}>
                                    <UnitMixOverviewCard
                                        values={unit as IUnitMix}
                                        onDuplicate={() => handleDuplicateOrAdd(unit.id)}
                                        onDelete={() => handleDelete(unit.id)}
                                        onUpdate={handleUpdate}
                                    />
                                </div>
                            ))}
                        </RenderIf>

                        <RenderIf isTrue={!isApartment}>
                            {tenantArray.map(tenant => (
                                <div className="col-md-6" key={tenant.id}>
                                    <TenantsOverviewCard
                                        values={tenant}
                                        onDuplicate={() => handleDuplicateOrAdd(tenant.id)}
                                        onDelete={() => handleDelete(tenant.id)}
                                        onUpdate={handleUpdate}
                                        count={tenantArray.length}
                                        operationStartDate={stepData?.operationStartDate ? new Date(stepData?.operationStartDate) : undefined}
                                        proFormaSaleDate={stepData?.proFormaSaleDate ? new Date(stepData?.proFormaSaleDate) : undefined}
                                    />
                                </div>
                            ))}
                        </RenderIf>

                        <div className={`col-md-${isApartment ? '4' : '6'} d-flex flex-column`}>
                            <NSCard
                                onClick={() => handleDuplicateOrAdd()}
                                className={`                               
                                cursor--pointer text-white align-items-center justify-content-center mb-2 flex-grow-1 
                                ${isApartment
            ? 'ProFormaWizardStepThree__addApartmentCard'
            : 'ProFormaWizardStepThree__addOtherCard'}
                            `}
                            >
                                <FontAwesomeIcon icon={faPlusCircle} size="lg" className="pb-1" />
                                <div>Add unit type</div>
                            </NSCard>
                        </div>
                    </div>
                    <NSCard className="NSCard--level-4">
                        <div className="d-flex justify-content-between text-white font-weight-bold p-2">
                            Total annual rental income
                            <div>{formatUSDCurrencyTwoDigits(incomeUnitTotals?.totalRentalIncome || 0)}</div>
                        </div>
                    </NSCard>
                </NSCard>

                {/* Other income */}
                <NSCard className="mt-2 p-2" title="Unit Mix">
                    <div className="d-flex justify-content-between">
                        <div>
                            <div className="ProFormaWizardStepThree__unitTitle">Other income</div>
                            <div className="ProFormaWizardStepOne__subtitle">Add additional income sources (e.g. RUBS, Parking)</div>
                        </div>
                        <div className="ProFormaWizardStepOne__subtitle align-self-center">
                            <NSSwitch
                                id="calculateToggle"
                                name="calculateToggle"
                                checked={showOtherIncomeToggle}
                                onChange={toggleCalculate}
                                label=""
                                containerClassName="text-dark"
                            />
                        </div>
                    </div>

                    <RenderIf isTrue={showOtherIncomeToggle}>
                        <div className="mt-2">
                            {incomeArray.map(income => (
                                <OtherIncomeCard
                                    key={income.id}
                                    values={income}
                                    onUpdate={handleUpdateIncome}
                                    onDelete={() => handleDeleteIncome(income.id)}
                                    totalIncomeUnitMixUnits={incomeUnitTotals?.totalIncomeUnits || 0}
                                />
                            ))}
                            <NSCard
                                onClick={() => handleAddOrDuplicateIncome()}
                                className="ProFormaWizardStepThree__addOtherIncomeCard cursor--pointer text-white align-items-center justify-content-center mb-2 flex-grow-1"
                            >
                                <div className="py-3">
                                    <FontAwesomeIcon icon={faPlus} className="pr-2" />
                                    Add other income
                                </div>
                            </NSCard>
                        </div>
                        <NSCard className="NSCard--level-4">
                            <div className="d-flex justify-content-between text-white font-weight-bold p-2">
                                Total other income:
                                <div>{formatUSDCurrencyTwoDigits(totalOtherIncome)}</div>
                            </div>
                        </NSCard>
                    </RenderIf>
                </NSCard>

                <div className="ProFormaWizardStepOne__separator" />

                <Expenses
                    values={expensesValues}
                    proFormaModelId={proFormaModelId}
                    setExpensesValues={setExpensesValues}
                    setExpensesDetail={setExpensesDetail}
                    showExpenseDetail={showExpenseDetailToggle}
                    setShowExpenseDetail={setShowExpenseDetailToggle}
                    initialExpenses={stepData?.operatingExpenses?.length !== 0 ? expensesDetail : INITIAL_EXPENSES}
                    totalIncomeUnits={incomeUnitTotals?.totalIncomeUnits || 0}
                    totalSFPerUnits={incomeUnitTotals?.totalSFPerUnits || 0}
                    boeDiscrepancies={boeDiscrepancies}
                />
            </ReadOnlyWrapper>
        </RenderIf>
    );
};

export default ProFormaWizardStepThree;
